import { IconLoader2 } from '@tabler/icons-react'
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { apiFetch } from '@/lib/api'

type SessionUser = {
  id: string
  email: string
  username: string
  displayName: string
  avatarUrl: string | null
  role: 'user' | 'moderator' | 'admin'
  createdAt: number
}

type AuthState =
  | { status: 'loading' }
  | { status: 'anonymous' }
  | { status: 'signedIn'; user: SessionUser }

type AuthCtx = {
  auth: AuthState
  reload: () => Promise<void>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthCtx | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [auth, setAuth] = useState<AuthState>({ status: 'loading' })

  const reload = useCallback(async () => {
    try {
      const payload = await apiFetch<{ user: SessionUser | null }>(`/api/auth/me`, { method: 'GET' })
      setAuth(payload.user ? { status: 'signedIn', user: payload.user } : { status: 'anonymous' })
    } catch {
      setAuth({ status: 'anonymous' })
    }
  }, [])

  useEffect(() => {
    void reload()
  }, [reload])

  const signOut = useCallback(async () => {
    await apiFetch<{ ok?: boolean }>(`/api/auth/sign-out`, {
      method: 'POST',
      json: {},
    }).catch(() => {})
    setAuth({ status: 'anonymous' })
  }, [])

  const ctx = useMemo<AuthCtx>(
    () => ({
      auth,
      reload,
      signOut,
    }),
    [auth, reload, signOut],
  )

  /* Single layout root so sibling nodes (overlay + app) participate in viewport flex height */
  return (
    <AuthContext.Provider value={ctx}>
      <div className="flex min-h-0 w-full flex-1 flex-col">{children}</div>
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthCtx {
  const ctx = useContext(AuthContext)

  if (!ctx) throw new Error('Missing AuthProvider.')
  return ctx
}

export function useSessionUser(): SessionUser | null {
  const { auth } = useAuth()
  return auth.status === 'signedIn' ? auth.user : null
}

/** True while the first (or in-flight) `/api/auth/me` check has not settled yet. */
export function useAuthLoading(): boolean {
  const { auth } = useAuth()
  return auth.status === 'loading'
}

/**
 * True only after `/api/auth/me` resolves with no session.
 * Unlike `!useSessionUser()`, this is never true during the initial loading phase.
 */
export function useConfirmedGuest(): boolean {
  const { auth } = useAuth()
  return auth.status === 'anonymous'
}

export function AuthGateOverlay() {
  const { auth } = useAuth()
  if (auth.status !== 'loading') return null

  return (
      <div className="fixed inset-0 z-[100] grid place-items-center bg-white/92">
      <div className="flex items-center gap-2 border-2 border-slate-900 bg-white px-5 py-3 text-[13px] font-bold">
        <IconLoader2 className="h-6 w-6 animate-spin text-nk-sea" aria-hidden="true" />
        <span className="font-medium text-nk-slate">Opening the harborside door…</span>
      </div>
    </div>
  )
}
